import type { Spreadable } from '../types/Spreadable';

type KeyOfUndefined<TObj> = {
  [K in keyof TObj]-?: undefined extends TObj[K] ? K : never;
}[keyof TObj];
type KeyOfNotUndefined<TObj> = {
  [K in keyof TObj]-?: undefined extends TObj[K] ? never : K;
}[keyof TObj];

/**
 * Remove keys from object that are undefined
 *
 * @example
 * omitUndefined({foo: 'a', bar: null, baz: undefined});
 * // -> {foo: 'a', bar: null}
 */
export const omitUndefined = <T extends Spreadable>(
  x: T,
): {
  [P in KeyOfUndefined<T>]?: T[P];
} & {
  [P in KeyOfNotUndefined<T>]: T[P];
} =>
  Object.entries(x).reduce((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key as keyof T] = value as T[keyof T];
    }
    return acc;
  }, {} as T);
